import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { BsInbox, BsPlusLg, BsArrowClockwise } from "react-icons/bs";
import { Skeleton } from "../../Components/Needed";
import { useAuth } from "../../Contexts/AuthenticateProvider";
import ticketApi from "../../Helpers/ticketApi";
import { PRIORITY_CLASS, PRIORITY_LABEL, STATUS_CLASS, assigneeText, raiserText, slaSummary, when } from "./shared";
import "../../Assets/css/tickets.css";

// Staff tickets (Phase 7.9): the hub pool, Mine, Appealed by me, and the tickets I raised to the admins.
const LIMIT = 20;

const TicketQueues = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canView = hasPermission("ticket.view") || hasPermission("ticket.handle");
    const queues = useMemo(() => [
        ...(canView ? [
            { key: "pool", label: "Pool", hint: "Customer tickets for your hub, and Account tickets, that nobody has taken yet." },
            { key: "mine", label: "Mine", hint: "Customer tickets you are handling." },
            { key: "appealed", label: "Appealed by me", hint: "Tickets you sent to the admins." },
        ] : []),
        { key: "staff", label: "My staff tickets", hint: "Tickets you raised to the admins. Only you and the admins see them." },
    ], [canView]);
    const [params, setParams] = useSearchParams();
    const queue = queues.some((q) => q.key === params.get("queue")) ? params.get("queue") : queues[0].key;
    const page = Math.max(Number(params.get("page")) || 0, 0);
    const [search, setSearch] = useState(params.get("search") || "");
    const [data, setData] = useState(null);
    const [counts, setCounts] = useState({});

    const set = (patch) => {
        const next = new URLSearchParams(params);
        Object.entries(patch).forEach(([k, v]) => (v === "" || v === null || v === undefined ? next.delete(k) : next.set(k, String(v))));
        setParams(next, { replace: true });
    };

    const load = useCallback(async () => {
        setData(null);
        const [list, c] = await Promise.all([
            ticketApi.queue(queue, { page, limit: LIMIT, ...(params.get("search") ? { search: params.get("search") } : {}) }),
            ticketApi.counts(),
        ]);
        if (!list.ok) { toast.error(list.message); setData({ rows: [], total: 0 }); } else setData(list.data);
        if (c.ok) setCounts(c.data.counts || {});
    }, [queue, page, params]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const timer = setInterval(() => { ticketApi.counts().then((c) => c.ok && setCounts(c.data.counts || {})); }, 30000);
        return () => clearInterval(timer);
    }, []);

    const total = data?.total || 0;
    const pages = Math.max(1, Math.ceil(total / LIMIT));
    const current = queues.find((q) => q.key === queue);
    const own = queue === "staff";

    return (
        <div className="tk-page">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h4 className="fw-semibold mb-1">Support tickets</h4>
                    <p className="text-muted mb-0">{canView ? "Customer tickets for your hub, and your own tickets to the admins." : "Your tickets to the admins."}</p>
                </div>
                <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-2" onClick={() => navigate("/tickets/new")}><BsPlusLg aria-hidden="true" /> Raise a ticket</button>
            </div>

            <div className="tk-tabs" role="tablist" aria-label="Queues">
                {queues.map((q) => (
                    <button key={q.key} type="button" role="tab" aria-selected={queue === q.key} className={`tk-tab ${queue === q.key ? "is-active" : ""}`} onClick={() => set({ queue: q.key, page: "" })}>
                        {q.label}{counts[q.key] ? <span className="tk-count">{counts[q.key]}</span> : null}
                    </button>
                ))}
            </div>
            <p className="small text-muted mt-2 mb-3">{current.hint}</p>

            <div className="d-flex flex-wrap gap-2 mb-3">
                <form className="d-flex gap-2" onSubmit={(e) => { e.preventDefault(); set({ search: search.trim(), page: "" }); }} role="search">
                    <label className="visually-hidden" htmlFor="tk-search">Search tickets</label>
                    <input id="tk-search" className="form-control form-control-sm" style={{ minWidth: 220 }} placeholder="Ticket ID, subject, booking" value={search} onChange={(e) => setSearch(e.target.value)} />
                    <button type="submit" className="btn btn-sm btn-dark">Search</button>
                </form>
                <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={load} aria-label="Refresh"><BsArrowClockwise aria-hidden="true" /></button>
            </div>

            {!data ? <Skeleton height={240} /> : data.rows.length === 0 ? (
                <div className="text-center text-muted py-5 border rounded-3 bg-white">
                    <BsInbox size={28} className="mb-2" aria-hidden="true" />
                    <div>{queue === "pool" ? "No tickets waiting. Nice." : "Nothing here."}</div>
                </div>
            ) : (
                <div className="table-responsive bg-white rounded-3 border">
                    <table className="table table-hover align-middle mb-0 tk-table">
                        <thead>
                            <tr>
                                <th scope="col">Ticket</th>
                                {own ? null : <th scope="col">Customer</th>}
                                <th scope="col">Status</th>
                                <th scope="col">{own ? "Handled by" : "With"}</th>
                                {own ? null : <th scope="col">SLA</th>}
                                <th scope="col">Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((t) => {
                                const sla = own ? null : slaSummary(t);
                                const unread = own ? t.unread : t.unread?.handler;
                                return (
                                    <tr key={t.ticketId} role="link" tabIndex={0} onClick={() => navigate(`/tickets/${t.ticketId}`)} onKeyDown={(e) => { if (e.key === "Enter") navigate(`/tickets/${t.ticketId}`); }}>
                                        <td>
                                            <div className="fw-semibold">{t.subject}{unread ? <span className="tk-dot" title="New message" /> : null}</div>
                                            <div className="small text-muted">{t.ticketId} · {t.category}{t.reason ? ` / ${t.reason}` : ""}{t.bookingId ? ` · ${t.bookingId}` : ""}</div>
                                        </td>
                                        {own ? null : <td className="small">{raiserText(t)}</td>}
                                        <td>
                                            <span className={`tk-status ${STATUS_CLASS[t.status] || ""}`}>{t.status}</span>
                                            {own ? null : <span className={`tk-priority ${PRIORITY_CLASS[t.priority] || ""}`}>{PRIORITY_LABEL[t.priority]}</span>}
                                        </td>
                                        <td className="small">{own ? (t.handler || "Admins") : assigneeText(t)}</td>
                                        {own ? null : <td className="small">{sla ? <span className={`tk-sla tk-sla-${sla.tone}`}>{sla.text}</span> : "-"}</td>}
                                        <td className="small text-muted">{when(t.updatedAt)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {total > LIMIT ? (
                <nav className="d-flex justify-content-between align-items-center mt-3" aria-label="Pages">
                    <span className="small text-muted">{total} tickets</span>
                    <div className="btn-group btn-group-sm">
                        <button type="button" className="btn btn-outline-secondary" disabled={page === 0} onClick={() => set({ page: page - 1 || "" })}>Previous</button>
                        <span className="btn btn-outline-secondary disabled">{page + 1} / {pages}</span>
                        <button type="button" className="btn btn-outline-secondary" disabled={page + 1 >= pages} onClick={() => set({ page: page + 1 })}>Next</button>
                    </div>
                </nav>
            ) : null}
        </div>
    );
};

export default TicketQueues;
